import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import { createAdminNotificationMeta } from '../../utils/adminNotificationMeta';
import { v4 as uuidv4 } from 'uuid';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { formatCustomTagsToLaTeX } from '../common/QuestionFormatter'; // Import the shared function
import MathTextRenderer from '../common/MathTextRenderer'; // Import the new component
import useExamProtection from '../../utils/useExamProtection';

// MathJax configuration
const config = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    // Define inline and display math delimiters explicitly
    inlineMath: [["$", "$"], ["\\(", "\\)"]], // $...$ and \(...\)
    displayMath: [["$$", "$$"], ["\\[", "\\]"]], // $$...$$ and \[...\]
    processEscapes: true,
    macros: { // Add support for \ce{} macro
      '\\ce': '{\\text{#1}}'
    }
  },
  options: { // Optional: Configure MathJax options if needed
    enableMenu: false, // Disable right-click MathJax menu
  },
};

function QuizBattleTake() {
  const { battleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Get participantId from URL or generate new one
  const participantId = searchParams.get('participant') || uuidv4();

   // Redirect to include participantId if not in URL
   useEffect(() => {
    if (!searchParams.get('participant')) {
      navigate(`/take-battle/${battleId}?participant=${participantId}`, { replace: true });
    }
  }, [battleId, participantId, searchParams, navigate]);


  const [battleData, setBattleData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [participantInitialized, setParticipantInitialized] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentFlaggedQuestionId, setCurrentFlaggedQuestionId] = useState(null);
  const [flagReason, setFlagReason] = useState('');

  const flagReasons = ['Incorrect answer', 'Unclear question', 'Grammatical error', 'Technical issue', 'Other'];

  console.log('QuizBattleTake render:', { battleId, participantId, currentUser: !!currentUser, loading, battleData: !!battleData, timeRemaining });

    // Modify the initialization useEffect to use battleId
    useEffect(() => {
        const initializeParticipant = async () => {
          if (!battleId || !participantId || !currentUser) return; // Ensure currentUser is available

          try {
            const participantRef = doc(db, 'public-quiz-participants', participantId);
            const participantDoc = await getDoc(participantRef);

            if (!participantDoc.exists()) {
              // Create new participant document if it doesn't exist
              await setDoc(participantRef, { battleId: battleId, userId: currentUser.uid, startedAt: serverTimestamp(), completed: false, answers: {}, score: 0, });
              console.log('Created new participant document for battle', participantId);
            } else {
               // If already completed, redirect to results (or leaderboard for battles)
              if (participantDoc.data().completed) {
                 const score = participantDoc.data().score;
                 navigate(`/battle-results/${battleId}?participant=${participantId}&score=${score}`); // Re-using quiz-results for now
                return;
              }
               const existingData = participantDoc.data();
               setAnswers(existingData.answers || {});
               if (!existingData.startedAt) { console.log('startedAt not found in existing participant document, setting now:', participantId); await updateDoc(participantRef, { startedAt: serverTimestamp() }); } else { console.log('startedAt found in existing participant document:', existingData.startedAt.toDate()); }
            }
            setParticipantInitialized(true);
          } catch (err) { console.error('Error initializing participant for battle:', err); setError('Failed to initialize Quiz Battle. Please try again.'); }
        };
        initializeParticipant();
      }, [battleId, participantId, navigate, currentUser]); // Added currentUser to dependencies

  // Fetch battle data
  useEffect(() => {
    let isMounted = true;
    async function fetchBattle() {
      if (!battleId) return;
      console.log('Fetching quiz battle:', battleId);
      try {
        const docRef = doc(db, 'quiz_battles', battleId);
        const docSnap = await getDoc(docRef);
        if (!isMounted) return;
        if (docSnap.exists()) { const battleData = docSnap.data(); setBattleData(battleData); console.log('Quiz Battle found'); } else { console.log('Quiz Battle not found'); setError('Quiz Battle not found'); }
      } catch (err) { console.error('Error fetching quiz battle:', err); if (isMounted) { setError(err.message); } } finally { if (isMounted) { setLoading(false); } }
    }
    fetchBattle();
    return () => { isMounted = false; };
  }, [battleId]); // Only depend on battleId

    // Wrap handleSubmit in useCallback
    const handleSubmit = useCallback(async () => {
        if (!battleData || !participantInitialized) { console.log('handleSubmit skipped: battleData or participant not initialized', { battleData: !!battleData, participantInitialized }); return; }
        console.log('handleSubmit called');
        try {
          console.log('Calculating score...');
          let score = 0;
          const battleQuestions = battleData.questions || [];
          battleQuestions.forEach((question, index) => { const selectedAnswer = answers[index]; if (selectedAnswer !== undefined && selectedAnswer === question.correctOption) { score++; } });
          const finalScore = battleQuestions.length === 0 ? 0 : Math.round((score / battleQuestions.length) * 100);
          console.log('Score calculated:', finalScore);
          const participantRef = doc(db, 'public-quiz-participants', participantId);
          const participantDoc = await getDoc(participantRef);
          if (!participantDoc.exists()) { console.error('Participant document not found for submission:', participantId); throw new Error('Participant document not found'); }
          console.log('Participant document found, fetching user data for name...');
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();
          console.log('User data fetched:', userData);
          const updateData = { completed: true, score: finalScore, answers: answers, completedAt: serverTimestamp(), userName: userData?.displayName || userData?.email || 'Anonymous', timeTaken: timeRemaining ? (battleData.timeLimit * 60) - (timeRemaining / 1000) : null, battleId: battleId, userId: currentUser.uid };
          console.log('Updating participant document with data:', updateData);
          await updateDoc(participantRef, updateData);
          console.log('Participant document updated successfully!');
          navigate(`/battle-results/${battleId}?participant=${participantId}&score=${finalScore}`);
        } catch (error) { console.error('Error submitting quiz battle:', error); setError('Failed to submit Quiz Battle. Please try again.'); }
      }, [answers, battleData, participantId, battleId, navigate, participantInitialized, currentUser, timeRemaining]);

    // Timer Logic - Adapted for battles
    useEffect(() => {
        if (!battleData?.timeLimit || !participantInitialized || isTimeUp) { console.log('Timer not starting/already stopped:', { timeLimit: battleData?.timeLimit, participantInitialized, isTimeUp }); return; }
        const initializeTimer = async () => {
             try {
                const participantRef = doc(db, 'public-quiz-participants', participantId);
                const participantDoc = await getDoc(participantRef);
                if (!participantDoc.exists()) { console.error('Participant document not found for timer initialization'); return; }
                const startTime = participantDoc.data().startedAt?.toDate();
                if (!startTime) { console.error('Start time not found in participant document for timer'); return; }
                const timeLimitMs = battleData.timeLimit * 60 * 1000;
                const endTime = new Date(startTime.getTime() + timeLimitMs);
                const remaining = endTime - new Date();
                 console.log('Timer initialized for battle:', { startTime, timeLimitMs, endTime, remaining });
                setTimeRemaining(Math.max(0, remaining));
                 if (remaining > 0) {
                    const timer = setInterval(() => { setTimeRemaining(prev => { if (prev === null) return null; const newTime = Math.max(0, prev - 1000); if (newTime === 0) { setIsTimeUp(true); clearInterval(timer); } return newTime; }); }, 1000);
                    return () => clearInterval(timer);
                 }
              } catch (err) { console.error('Error initializing timer for battle:', err); }
        };
        initializeTimer();
     }, [battleData, participantId, participantInitialized, isTimeUp]); // Added battleData and isTimeUp

     // Submit quiz when time is up
     useEffect(() => { if (isTimeUp) { console.log('Time is up, submitting quiz battle...'); handleSubmit(); } }, [isTimeUp, handleSubmit]);

    // Function to handle option selection
    const handleAnswerSelect = (questionIndex, optionIndex) => { setAnswers(prevAnswers => ({ ...prevAnswers, [questionIndex]: optionIndex, })); };

      // Function to navigate to the next question
      const handleNextQuestion = () => { if (currentQuestionIndex < battleData.questions.length - 1) { setCurrentQuestionIndex(prevIndex => prevIndex + 1); } };

      // Function to navigate to the previous question
      const handlePreviousQuestion = () => { if (currentQuestionIndex > 0) { setCurrentQuestionIndex(prevIndex => prevIndex - 1); } };

     // Add this function to handle flagging questions
     const handleFlagQuestion = (questionId) => { setCurrentFlaggedQuestionId(questionId); setShowFlagModal(true); };

    // Add this function to submit the flag
    const submitFlag = async () => {
      if (!flagReason || !currentFlaggedQuestionId || !battleData) return;
      try {
        await addDoc(collection(db, 'flagged_questions'), {
          questionId: currentFlaggedQuestionId,
          userId: auth.currentUser.uid,
          reason: flagReason,
          timestamp: serverTimestamp(),
          status: 'pending',
          battleId: battleId,
          questionIndex: currentQuestionIndex,
          userAnswer: answers[currentQuestionIndex] ?? null,
          correctAnswer: battleData.questions[currentQuestionIndex].correctOption,
          ...createAdminNotificationMeta({
            type: 'flagged_question',
            summary: `Flagged battle question in ${battleId} by ${auth.currentUser.displayName || auth.currentUser.email || 'a user'}`,
          }),
        });
        setShowFlagModal(false); setFlagReason(''); setCurrentFlaggedQuestionId(null);
      } catch (error) { console.error('Error submitting flag for battle:', error); }
    };

    // Exam protection: disable copy/paste, track focus/visibility, autosave, and auto-submit on violations
    useExamProtection({
      enabled: Boolean(battleData && participantInitialized),
      getState: () => ({ answers, timeLeft: timeRemaining }),
      saveDraft: async ({ answers: draftAnswers, timeLeft }) => {
        try {
          if (!participantInitialized) return;
          const participantRef = doc(db, 'public-quiz-participants', participantId);
          await updateDoc(participantRef, {
            answers: draftAnswers || {},
            draftSavedAt: serverTimestamp(),
            timeRemainingMs: typeof timeLeft === 'number' ? timeLeft : null,
          });
        } catch (err) {
          console.error('Autosave draft failed:', err);
        }
      },
      onViolation: async (type, count) => {
        try {
          await addDoc(collection(db, 'exam_violations'), {
            battleId,
            participantId,
            userId: currentUser?.uid || auth.currentUser?.uid || null,
            type,
            count,
            timestamp: serverTimestamp(),
          });
        } catch (err) {
          console.error('Failed to record violation:', err);
        }
      },
      onAutoSubmit: () => {
        // Best-effort: submit immediately when violations exceed threshold
        if (!isTimeUp) {
          handleSubmit();
        }
      },
      // Slightly stricter defaults for exams
      autosaveMs: 15000,
      maxViolationsBeforeAutoSubmit: 3,
      warnBeforeUnload: true,
      countOnBlur: true,
      violationDebounceMs: 20000,
      violationThrottleMs: 5000,
    });

     // Render logic
     if (loading) { return <div className="flex justify-center items-center h-screen">Loading Quiz Battle...</div>; }
     if (error) { return <div className="flex flex-col items-center justify-center h-screen text-red-500">{error}</div>; }
     if (!battleData || !participantInitialized) { return <div className="flex justify-center items-center h-screen text-gray-500">Initializing Quiz Battle...</div>; }
     const currentQuestion = battleData.questions?.[currentQuestionIndex];
     if (!currentQuestion) { return <div className="flex justify-center items-center h-screen text-gray-500">No questions available for this battle.</div>; }
     const formatTime = (ms) => { const totalSeconds = Math.floor(ms / 1000); const minutes = Math.floor(totalSeconds / 60); const seconds = totalSeconds % 60; return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`; };

    return (
      <MathJaxContext config={config}>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4 text-center">Quiz Battle: {battleData.title}</h1>
        {/* Timer Display */}
          {battleData.timeLimit > 0 && timeRemaining !== null && ( <div className={`text-center text-xl font-semibold mb-4 ${timeRemaining < 60000 ? 'text-red-600' : ''}`}> Time Remaining: {formatTime(timeRemaining)} </div> )}
        {/* Question Navigation and Flag Button */}
        <div className="flex justify-between items-center mb-4">
            <span>Question {currentQuestionIndex + 1} of {battleData.questions.length}</span>
               <button onClick={() => handleFlagQuestion(currentQuestion.id)} className={`p-2 rounded-full ${flaggedQuestions.has(currentQuestion.id) ? 'text-red-500 cursor-not-allowed' : 'text-gray-400 hover:text-red-500'}`} title="Flag Question" disabled={flaggedQuestions.has(currentQuestion.id)}> <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"> <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /> </svg> </button>
        </div>
        {/* Current Question */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">
              <MathTextRenderer text={formatCustomTagsToLaTeX(currentQuestion.question)} />
            </h3>
          {/* Options */}
            <div className="space-y-3"
               key={currentQuestionIndex}
            >
              {currentQuestion.options.map((option, optionIndex) => (
                <button 
                  key={optionIndex} 
                  onClick={() => handleAnswerSelect(currentQuestionIndex, optionIndex)} 
                  className={`w-full text-left p-3 rounded-md border ${
                    answers[currentQuestionIndex] === optionIndex 
                      ? 'bg-[rgb(130,88,18)] text-white border-[rgb(130,88,18)]' 
                      : 'bg-gray-100 hover:bg-gray-200 border-gray-300'
                  }`} 
                  disabled={isTimeUp}
                >
                  {String.fromCharCode(65 + optionIndex)}. {' '}
                  <MathTextRenderer text={formatCustomTagsToLaTeX(option)} />
                </button>
              ))}
            </div>
          </div>
        {/* Navigation Buttons */}
        <div className="flex justify-between mt-4">
            <button onClick={handlePreviousQuestion} disabled={currentQuestionIndex === 0 || isTimeUp} className="px-6 py-2 bg-gray-300 rounded-md hover:bg-gray-400 disabled:opacity-50"> Previous </button>
            {currentQuestionIndex < battleData.questions.length - 1 ? ( <button onClick={handleNextQuestion} disabled={isTimeUp} className="px-6 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,68,0)] disabled:opacity-50"> Next </button> ) : ( <button onClick={handleSubmit} disabled={isTimeUp} className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"> Submit Battle </button> )}
          </div>
           {/* Flag Question Modal */}
           {showFlagModal && ( <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"> <div className="bg-white rounded-lg p-6 max-w-md w-full"> <h3 className="text-xl font-semibold mb-4">Flag Question</h3> <div className="mb-4"> <label className="block text-sm font-medium text-gray-700 mb-2"> Reason for Flagging </label> <select value={flagReason} onChange={(e) => setFlagReason(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"> <option value="">Select a reason</option> {flagReasons.map((reason) => ( <option key={reason} value={reason}> {reason} </option> ))} </select> </div> <div className="flex justify-end space-x-4"> <button onClick={() => { setShowFlagModal(false); setFlagReason(''); setCurrentFlaggedQuestionId(null); }} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"> Cancel </button> <button onClick={submitFlag} disabled={!flagReason} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"> Submit Flag </button> </div> </div> </div> )}
        </div>
      </MathJaxContext>
    );
  }

export default QuizBattleTake; 